export type StaticGeometryRowModel = {
    key: unknown
    estimatedHeight: number
    staticSignature: string | null
    resizeIntentSignature: string | null
}

export type StaticGeometryRowMeasurement = {
    height: number
    signature: string
}

export type StaticGeometryRowMeasurementSample = {
    height: number
    observationTargets: Element[]
}

const MEASUREMENT_TOLERANCE_PX = 1
const CONTENT_BOTTOM_BUFFER_PX = 2
const INTENTIONAL_CLIP_SELECTOR = '[data-static-geometry-intentional-clip="true"]'
const INTRINSIC_CONTENT_SELECTOR = [
    '[data-answer-row-id]',
    '.answer-map-container',
    '.knov-static-answer-body',
    '.ProseMirror',
    '.tiptap-prose',
    '[data-testid="answer-link-preview-card"]',
    '[class*="payloadContainer"]',
    '[class*="urlAnswerContainer"]',
    '[class*="embedContainer"]',
    '[class*="filesContainer"]',
    '[class*="answerControlContainer"]',
    '[class*="voteControlsContainer"]',
    'img',
    'video',
    'audio',
    'canvas',
    'iframe',
    INTENTIONAL_CLIP_SELECTOR,
].join(',')

function isInsideIntentionalClip(element: Element) {
    const intentionalClip = element.closest(INTENTIONAL_CLIP_SELECTOR)
    return intentionalClip != null && intentionalClip !== element
}

function renderedContentBottom(root: HTMLElement, candidates: Element[]) {
    const rootRect = root.getBoundingClientRect()
    if (!Number.isFinite(rootRect.top)) return 0

    let bottom = rootRect.top
    const rememberBottom = (candidateBottom: number) => {
        if (Number.isFinite(candidateBottom) && candidateBottom > bottom) {
            bottom = candidateBottom
        }
    }

    candidates.forEach(candidate => {
        if (!(candidate instanceof HTMLElement) || isInsideIntentionalClip(candidate)) return

        const candidateRect = candidate.getBoundingClientRect()
        rememberBottom(candidateRect.bottom)

        if (
            !candidate.matches(INTENTIONAL_CLIP_SELECTOR) &&
            candidate.scrollHeight > candidate.clientHeight + MEASUREMENT_TOLERANCE_PX
        ) {
            rememberBottom(candidateRect.top + candidate.scrollHeight)
        }
    })

    root.querySelectorAll<HTMLElement>(
        '.knov-static-answer-body, .ProseMirror, .tiptap-prose',
    ).forEach(content => {
        if (isInsideIntentionalClip(content)) return

        const range = content.ownerDocument?.createRange?.()
        if (!range || typeof range.getBoundingClientRect !== 'function') return

        range.selectNodeContents(content)
        rememberBottom(range.getBoundingClientRect().bottom)
        range.detach?.()
    })

    const measuredHeight = Math.ceil(bottom - rootRect.top + CONTENT_BOTTOM_BUFFER_PX)
    return Number.isFinite(measuredHeight) && measuredHeight > 0 ? measuredHeight : 0
}

export function readStaticQuestRowMeasurement(
    row: HTMLElement,
): StaticGeometryRowMeasurementSample | null {
    const answerRows = Array.from(row.querySelectorAll<HTMLElement>('[data-answer-row-id]'))
    if (answerRows.length === 0) return null

    const candidates = Array.from(row.querySelectorAll<HTMLElement>(INTRINSIC_CONTENT_SELECTOR))
    const questFrames = Array.from(
        row.querySelectorAll<HTMLElement>('[data-qa-scroll-quest-frame="true"]'),
    )
    questFrames.forEach(frame => {
        if (frame.scrollHeight <= frame.clientHeight + MEASUREMENT_TOLERANCE_PX) return

        candidates.push(frame)
    })

    const height = renderedContentBottom(row, candidates)
    return height > 0 ? { height, observationTargets: answerRows } : null
}

export function readStaticAnswerRowMeasurement(
    row: HTMLElement,
): StaticGeometryRowMeasurementSample | null {
    const content = row.querySelector<HTMLElement>('.answer-map-container')
    if (!content) return null

    const candidates = [
        content,
        ...Array.from(content.querySelectorAll<HTMLElement>(INTRINSIC_CONTENT_SELECTOR)),
    ]
    const height = renderedContentBottom(row, candidates)
    return height > 0 ? { height, observationTargets: [content] } : null
}

export function applyStaticGeometryRowMeasurements<T extends StaticGeometryRowModel>(
    rows: T[],
    measurements: ReadonlyMap<string, StaticGeometryRowMeasurement>,
): T[] {
    let changed = false
    const measuredRows = rows.map(row => {
        const measurement = measurements.get(String(row.key))
        if (
            row.staticSignature == null ||
            measurement?.signature !== row.staticSignature ||
            Math.abs(measurement.height - row.estimatedHeight) <= MEASUREMENT_TOLERANCE_PX
        ) {
            return row
        }

        changed = true
        return {
            ...row,
            estimatedHeight: measurement.height,
            staticSignature: `${row.staticSignature}:measured:${measurement.height}`,
        }
    })

    return changed ? measuredRows : rows
}
